import { Component, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { Watchlist, BenchmarkPortfolio, PortfolioType } from '../../monitoring.model';
import { WatchlistSidetabComponent } from '../watchlist-sidetab/watchlist-sidetab.component';
import { WatchlistMainPageComponent } from '../watchlist-main-page/watchlist-main-page.component';
import { MonitoringService } from '../../monitoring.service';
import { BenchmarkService } from '../../benchmark.service';
import { MainNavbarComponent } from 'src/app/core/components/main-navbar/main-navbar.component';
import { MatDialog } from '@angular/material/dialog';
import { WatchlistEditCreateDialogComponent } from '../watchlist-edit-dialog/watchlist-edit-create-dialog.component';
import { WatchlistStore } from '../../store/watchlist.store';

@Component({
  selector: 'app-monitoring-dashboard',
  standalone: true,
  imports: [
    CommonModule,
    WatchlistSidetabComponent,
    WatchlistMainPageComponent,
    MainNavbarComponent
  ],
  templateUrl: './monitoring-dashboard.component.html',
  styleUrl: './monitoring-dashboard.component.css'
})
export class MonitoringDashboardComponent implements OnInit {
  userWatchlists: Watchlist[] = [];
  benchmarks: BenchmarkPortfolio[] = [];
  selectedWatchlist: Watchlist | null = null;
  isLoading = false;
  isLoadingBenchmarks = false;
  error: string | null = null;
  userId: string | null = null;
  isSidebarCollapsed = false;

  // Deep link parameters
  private deepLinkType: 'watchlist' | 'benchmark' | null = null;
  private deepLinkId: number | null = null;
  private deepLinkHandled = false;

  constructor(
    private monitoringService: MonitoringService,
    private benchmarkService: BenchmarkService,
    private dialog: MatDialog,
    private watchlistStore: WatchlistStore,
    private route: ActivatedRoute
  ) { }

  // ngOnInit() {
  //   // this.userId = localStorage.getItem('user_id');
  //   // this.isLoading = true;
  //   // this.loadUserWatchlists();
  //   // this.loadBenchmarks();

  //   this.userId = localStorage.getItem('user_id');

  //   const currentSection = this.watchlistStore.getCurrentActiveSection();
  //   if (currentSection === 'watchlist') {
  //   if (!this.userWatchlists || this.userWatchlists.length === 0) {
  //     this.loadUserWatchlists();
  //   }}
  //   else if (currentSection === 'benchmark') {
  //     if (!this.benchmarks || this.benchmarks.length === 0) {
  //       this.loadBenchmarks();
  //     }
  //   }

  // }

  ngOnInit() {
    this.userId = localStorage.getItem('user_id');

    // Check for deep link query parameters (from email notifications)
    this.route.queryParams.subscribe(params => {
      if (params['type'] && params['id']) {
        this.deepLinkType = params['type'] as 'watchlist' | 'benchmark';
        this.deepLinkId = Number(params['id']);
        this.deepLinkHandled = false;
      }
    });

    const activeSection = this.watchlistStore.getCurrentActiveSection();
    const hasActiveWatchlist = this.watchlistStore.getCurrentActiveWatchlist();
    const hasActiveBenchmark = this.watchlistStore.getCurrentActiveBenchmark();

    // If we have deep link params, prioritize loading that section
    if (this.deepLinkType === 'watchlist') {
      this.loadUserWatchlists();
      setTimeout(() => this.loadBenchmarks(), 100);
    } else if (this.deepLinkType === 'benchmark') {
      this.loadBenchmarks();
      setTimeout(() => this.loadUserWatchlists(), 100);
    }
    // If we have an active session, prioritize loading that section's list
    else if (activeSection === 'watchlist' || hasActiveWatchlist) {
      // Load watchlists first (active section)
      this.loadUserWatchlists();
      // Load benchmarks lazily (for sidebar, but not blocking)
      setTimeout(() => this.loadBenchmarks(), 100);
    } else if (activeSection === 'benchmark' || hasActiveBenchmark) {
      // Load benchmarks first (active section)
      this.loadBenchmarks();
      // Load watchlists lazily (for sidebar, but not blocking)
      setTimeout(() => this.loadUserWatchlists(), 100);
    } else {
      // No active session (first visit) - need both for default landing logic
      this.loadUserWatchlists();
      this.loadBenchmarks();
    }
  }

  loadUserWatchlists() {
    this.isLoading = true;
    
    this.monitoringService.getUserWatchlists(Number(this.userId)).subscribe({
      next: (res) => {
        this.userWatchlists = res.payload;
        this.isLoading = false;
        this.setDefaultLanding();
      },
      error: (error) => {
        this.error = 'Failed to load watchlists';
        this.isLoading = false;
        console.error('Error loading watchlists:', error);
        this.setDefaultLanding();
      }
    });
  }

  loadBenchmarks() {
    this.isLoadingBenchmarks = true;
    this.benchmarkService.getAllBenchmarks().subscribe({
      next: (res) => {
        this.benchmarks = res.payload;
        this.isLoadingBenchmarks = false;
        const current = this.watchlistStore.getCurrentActiveBenchmark();
        const freshById = current ? res.payload?.find((b: BenchmarkPortfolio) => b.benchmark_id === current.benchmark_id) : null;
        if (current && freshById) {
          this.watchlistStore.setActiveBenchmark(freshById);
        }
        this.setDefaultLanding();
      },
      error: (error) => {
        console.error('Error loading benchmarks:', error);
        this.isLoadingBenchmarks = false;
        this.setDefaultLanding();
      }
    });
  }

  setDefaultLanding() {
    // Only set default if both watchlists and benchmarks have finished loading
    if (this.isLoading || this.isLoadingBenchmarks) {
      return;
    }

    // Handle deep link auto-selection first (from email notifications)
    if (this.deepLinkType && this.deepLinkId && !this.deepLinkHandled) {
      this.deepLinkHandled = true;

      if (this.deepLinkType === 'watchlist') {
        const targetWatchlist = this.userWatchlists.find(w => w.watchlist_id === this.deepLinkId);
        if (targetWatchlist) {
          this.watchlistStore.setActiveSection('watchlist');
          this.selectedWatchlist = targetWatchlist;
          this.watchlistStore.setActiveWatchlist(targetWatchlist);
          return;
        }
      } else if (this.deepLinkType === 'benchmark') {
        const targetBenchmark = this.benchmarks.find(b => b.benchmark_id === this.deepLinkId);
        if (targetBenchmark) {
          this.watchlistStore.setActiveSection('benchmark');
          this.watchlistStore.setActiveBenchmark(targetBenchmark);
          return;
        }
      }
      // If deep link target not found, fall through to default logic
    }

    const currentWatchlist = this.watchlistStore.getCurrentActiveWatchlist();
    const currentBenchmark = this.watchlistStore.getCurrentActiveBenchmark();

    // If something is already selected, don't change it
    if (currentWatchlist || currentBenchmark) {
      return;
    }

    // Default landing logic: watchlists if available, else benchmarks
    if (this.userWatchlists.length > 0) {
      this.watchlistStore.setActiveSection('watchlist');
      this.selectedWatchlist = this.userWatchlists[0];
      this.watchlistStore.setActiveWatchlist(this.selectedWatchlist);
    } else if (this.benchmarks.length > 0) {
      this.watchlistStore.setActiveSection('benchmark');
      const firstBenchmark = this.benchmarks[0];
      this.watchlistStore.setActiveBenchmark(firstBenchmark);
    }
  }

  onSectionChanged(section: PortfolioType) {
    // Handle section change from sidebar
    if (section === 'watchlist' && this.userWatchlists.length > 0) {
      if (!this.watchlistStore.getCurrentActiveWatchlist()) {
        this.watchlistStore.setActiveWatchlist(this.userWatchlists[0]);
      }
    } else if (section === 'benchmark' && this.benchmarks.length > 0) {
      if (!this.watchlistStore.getCurrentActiveBenchmark()) {
        this.watchlistStore.setActiveBenchmark(this.benchmarks[0]);
      }
    }
  }


  onCreateWatchlist(): void {
    const dialogRef = this.dialog.open(WatchlistEditCreateDialogComponent, {
      width: '400px',
      data: { mode: 'create', watchlist_count: this.userWatchlists.length }
    });
    dialogRef.afterClosed().subscribe(result => {
      this.loadUserWatchlists();
      if (result?.payload) {
        this.selectedWatchlist = result.payload;
        this.watchlistStore.setActiveWatchlist(this.selectedWatchlist);
      }
    });
  }
  onEditWatchlist(watchlist: Watchlist) {
    const dialogRef = this.dialog.open(WatchlistEditCreateDialogComponent, {
      width: '400px',
      data: { mode: 'edit', watchlist: watchlist }
    });
    dialogRef.afterClosed().subscribe(result => {
      this.loadUserWatchlists();
      if (result?.payload) {
        this.selectedWatchlist = result.payload;
        this.watchlistStore.setActiveWatchlist(this.selectedWatchlist);
      }
    });
  }

  onDeleteWatchlist(watchlist: Watchlist) {
    this.monitoringService.deleteWatchlist(watchlist.watchlist_id).subscribe({
      next: (res) => {
        //remove the local storage selected watchlist
        localStorage.removeItem('selectedWatchlist');
        this.watchlistStore.setActiveWatchlist(null);
        this.loadUserWatchlists();
      },
      error: (error) => {
        this.error = 'Failed to delete watchlist';
        this.loadUserWatchlists();
      }
    });
  }

  onWatchlistUpdated() {
    this.loadUserWatchlists();
  }

  onSidebarCollapsed(collapsed: boolean) {
    this.isSidebarCollapsed = collapsed;
  }

  /** Refetch benchmarks when tab/window gains focus so admin edits are visible without reload */
  @HostListener('window:focus')
  onWindowFocus() {
    if (this.watchlistStore.getCurrentActiveSection() === 'benchmark' || this.watchlistStore.getCurrentActiveBenchmark()) {
      this.loadBenchmarks();
    }
  }
}
